/**
 * Client HTTP de l'API applicative.
 *
 * Trois responsabilités, et rien d'autre : porter le jeton, déballer l'enveloppe `{data, error, meta}`,
 * et traduire une erreur du serveur en `ErreurApi`. Aucune règle métier n'est implémentée ici — les
 * seuils, les indicateurs et le cloisonnement restent entièrement côté serveur
 * (`docs/agent-spec/23-DO-NOT.md`).
 */
import { ErreurApi, MESSAGE_INDISPONIBLE } from "./erreurs";
import type { Pagination } from "./types";

export type Reponse<T> = { data: T; meta?: Pagination };

/** Fournisseur de jeton, injecté pour que le client ne dépende pas du module d'authentification. */
export type FournisseurDeJeton = () => Promise<string | null>;

let fournirLeJeton: FournisseurDeJeton = async () => null;

export function definirFournisseurDeJeton(fournisseur: FournisseurDeJeton): void {
  fournirLeJeton = fournisseur;
}

function baseApi(): string {
  const base = import.meta.env.VITE_API_URL;
  if (!base) {
    throw new Error(
      "VITE_API_URL n'est pas défini : copier `.env.example` en `.env.local` (voir le README).",
    );
  }
  return base.replace(/\/$/, "");
}

/**
 * Construit une chaîne de requête à partir de paramètres éventuellement absents.
 *
 * Les clés de filtre gardent leurs crochets (`filter[client]`) : c'est la convention du contrat, et
 * `URLSearchParams` les encode correctement.
 */
export function requete(parametres: Record<string, string | number | null | undefined>): string {
  const utiles = Object.entries(parametres).filter(
    ([, valeur]) => valeur !== null && valeur !== undefined && valeur !== "",
  );
  if (utiles.length === 0) return "";
  return "?" + new URLSearchParams(utiles.map(([cle, valeur]) => [cle, String(valeur)])).toString();
}

async function appeler<T>(
  chemin: string,
  options: { methode?: string; corps?: unknown } = {},
): Promise<Reponse<T>> {
  const jeton = await fournirLeJeton();
  const entetes: Record<string, string> = {};
  if (jeton) entetes.Authorization = `Bearer ${jeton}`;
  if (options.corps !== undefined) entetes["Content-Type"] = "application/json";

  const reponse = await fetch(baseApi() + chemin, {
    method: options.methode ?? "GET",
    headers: entetes,
    body: options.corps === undefined ? undefined : JSON.stringify(options.corps),
  });

  // `204` et un corps vide : rien à déballer. Le cas ne se présente pas aujourd'hui, mais le lire
  // comme du JSON produirait une erreur de parsing trompeuse si une route y venait.
  const texte = await reponse.text();
  const corps = texte ? (JSON.parse(texte) as Record<string, unknown>) : {};

  if (!reponse.ok) {
    const erreur = corps.error as
      | { code?: string; message?: string; details?: { field?: string; issue?: string }[] }
      | undefined;
    throw new ErreurApi(
      reponse.status,
      erreur?.code ?? "INTERNAL_ERROR",
      erreur?.message ?? MESSAGE_INDISPONIBLE,
      erreur?.details ?? [],
    );
  }

  return { data: corps.data as T, meta: corps.meta as Pagination | undefined };
}

export const api = {
  lire: <T>(chemin: string) => appeler<T>(chemin),
  creer: <T>(chemin: string, corps?: unknown) => appeler<T>(chemin, { methode: "POST", corps }),
  remplacer: <T>(chemin: string, corps?: unknown) => appeler<T>(chemin, { methode: "PUT", corps }),
  modifier: <T>(chemin: string, corps?: unknown) => appeler<T>(chemin, { methode: "PATCH", corps }),
  supprimer: <T>(chemin: string) => appeler<T>(chemin, { methode: "DELETE" }),
};
